import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';

import { MODULE_CATALOG } from './moduleCatalog';

function DraggableItem({ type, label, onAdd, presetProps }){
  const id = `palette:${type}:${label}`;
  const { attributes, listeners, setNodeRef } = useDraggable({ id, data: { kind:'palette', type, presetProps } });
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={()=>{ if(onAdd) onAdd(type, presetProps); }}
      className='w-full text-left rounded border bg-background hover:bg-blue-50 focus:outline-none'>
      <Preview type={type} />
      <div className='px-2 py-1 text-[10px] text-slate-600'>{label}</div>
    </button>
  );
}

export function BuilderPalette({ onAdd, allowed, draggable }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const filtered = React.useMemo(() => (allowed ? MODULE_CATALOG.filter(c => allowed.includes(c.type)) : MODULE_CATALOG), [allowed]);
  const searched = React.useMemo(() => {
    if (!searchTerm.trim()) return filtered;
    const term = searchTerm.toLowerCase();
    return filtered.filter(c => c.label.toLowerCase().includes(term) || c.type.toLowerCase().includes(term) || (c.category||'').toLowerCase().includes(term));
  }, [filtered, searchTerm]);
  const byCat = React.useMemo(() => {
    const map = new Map();
    for (const c of searched) {
      const k = c.category || 'Andere';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    }
    return Array.from(map.entries()).map(([cat, items]) => ({ cat, items }));
  }, [searched]);

  return (
    <div className='border-r w-full bg-background flex flex-col h-full'>
      <div className='px-3 py-2 text-[11px] font-semibold border-b bg-slate-50'>Palette</div>
      <div className='px-2 pt-2'>
        <input 
          type='text'
          value={searchTerm}
          onChange={(e)=>setSearchTerm(e.target.value)}
          placeholder={t('common.searchPlaceholder')}
          className='w-full text-xs px-2 py-1 border rounded outline-none focus:border-blue-400'
        />
      </div>
      <div className='p-2 pb-8 space-y-2 flex-1 min-h-0 overflow-y-auto'>
        {byCat.map(group => (
          <Accordion key={group.cat} title={group.cat} defaultOpen={group.cat==='Layout' || group.cat==='Inhalte'}>
            <div className='grid grid-cols-2 gap-2'>
              {group.items.map(c => (
                draggable
                  ? <DraggableItem key={c.type} type={c.type} label={c.label} onAdd={onAdd} />
                  : <button key={c.type} onClick={()=>onAdd(c.type)} className='w-full text-left rounded border bg-background hover:bg-blue-50'>
                      <Preview type={c.type} />
                      <div className='px-2 py-1 text-[10px] text-slate-600'>{c.label}</div>
                    </button>
              ))}
            </div>
          </Accordion>
        ))}
      </div>
    </div>
  );
}

        function Preview({ type }){
          const box = (extra='') => <div className={`h-2 rounded bg-slate-300 ${extra}`} />;
          const line = (w='w-4/5') => <div className={`h-[3px] rounded bg-slate-400 ${w}`} />;
          switch(type){
            case 'section': return <div className='p-2 rounded bg-slate-50 border w-full h-12 flex items-center justify-center text-[10px] text-slate-500 overflow-hidden'>Section</div>;
            case 'container': return <div className='p-2 rounded border w-full h-12 flex items-center justify-center text-[10px] text-slate-500 overflow-hidden'>Container</div>;
            case 'card': return (
              <div className='p-2 rounded border shadow-sm w-full h-12 flex flex-col justify-center gap-1 overflow-hidden'>
                {line('w-3/5')}<div className='flex gap-1'>{box('w-1/3')} {box('w-1/4')} {box('w-1/5')}</div>
              </div>
            );
            case 'row': return (
              <div className='p-2 rounded border w-full h-12 flex items-center gap-1 overflow-hidden'>
                <div className='w-4 h-4 bg-slate-300 rounded' />
                <div className='w-4 h-4 bg-slate-300 rounded' />
                <div className='w-4 h-4 bg-slate-300 rounded' />
              </div>
            );
            case 'divider': return <div className='w-full h-12 flex items-center overflow-hidden'><div className='w-full border-t border-slate-300' /></div>;
            case 'spacer': return <div className='w-full h-12 flex items-center justify-center text-[10px] text-slate-400 overflow-hidden'>Abstand</div>;
            case 'heading': return (
              <div className='p-2 rounded border w-full h-12 flex flex-col justify-center gap-1 overflow-hidden'>
                <div className='h-[5px] bg-slate-700 rounded w-4/5' />
                <div className='h-[3px] bg-slate-400 rounded w-2/5' />
              </div>
            );
            case 'text': return (
              <div className='p-2 rounded border w-full h-12 flex flex-col justify-center gap-[3px] overflow-hidden'>
                {line('w-4/5')}{line('w-3/5')}{line('w-2/5')}
              </div>
            );
            case 'button': return <div className='w-full h-12 flex items-center justify-center overflow-hidden'><div className='px-3 py-1 text-[10px] rounded bg-slate-800 text-white'>Button</div></div>;
            case 'banner': return <div className='w-full h-12 rounded border bg-slate-100 flex items-center justify-center text-[10px] text-slate-500 overflow-hidden'>Banner</div>;
              case 'image': return (
                <div className='w-full h-12 rounded border bg-background flex items-center justify-center overflow-hidden'>
                  <div className='w-16 h-8 bg-slate-200 rounded border' />
                </div>
              );
              case 'video': return (
                <div className='w-full h-12 rounded border bg-background flex items-center justify-center overflow-hidden'>
                  <div className='w-16 h-8 bg-slate-200 rounded border relative'>
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <div className='w-0 h-0 border-l-8 border-y-6 border-y-transparent border-l-slate-500' />
                    </div>
                  </div>
                </div>
              );
            case 'companyLogo': return <div className='w-full h-12 flex items-center justify-center overflow-hidden'><div className='w-6 h-6 rounded-full bg-slate-300 border' /></div>;
            case 'companyBrand': return (
              <div className='w-full h-12 flex items-center gap-2 px-2 overflow-hidden'>
                <div className='w-6 h-6 rounded-full bg-slate-300 border shrink-0' />
                <div className='flex flex-col flex-1 min-w-0'>
                  <div className='h-[6px] bg-slate-500 rounded w-2/5' />
                  <div className='h-[4px] bg-slate-300 rounded w-3/5 mt-[2px]' />
                </div>
              </div>
            );
            case 'socials': return (
              <div className='w-full h-12 flex items-center justify-center gap-2 overflow-hidden'>
                <div className='w-5 h-5 rounded-full bg-blue-500' />
                <div className='w-5 h-5 rounded-full bg-sky-500' />
                <div className='w-5 h-5 rounded-full bg-pink-500' />
              </div>
            );
            case 'columns1': return <div className='w-full h-12 border rounded bg-slate-50 overflow-hidden' />;
            case 'columns2': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
              </div>
            );
            case 'columns2_2575': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='w-1/4 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
              </div>
            );
            case 'columns2_7525': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='w-1/4 border rounded bg-slate-50' />
              </div>
            );
            case 'columns3': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
              </div>
            );
            case 'columns3_252550': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='w-1/4 border rounded bg-slate-50' />
                <div className='w-1/4 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
              </div>
            );
            case 'columns3_255025': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='w-1/4 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='w-1/4 border rounded bg-slate-50' />
              </div>
            );
            case 'columns3_502525': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='w-1/4 border rounded bg-slate-50' />
                <div className='w-1/4 border rounded bg-slate-50' />
              </div>
            );
            case 'columns4': return (
              <div className='w-full h-12 flex gap-1 overflow-hidden'>
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
                <div className='flex-1 border rounded bg-slate-50' />
              </div>
            );
            case 'codeRequestForm': return (
              <div className='p-2 rounded border w-full h-12 flex flex-col justify-center gap-1 overflow-hidden'>
                <div className='h-[6px] rounded bg-slate-300 w-4/5' />
                <div className='h-[6px] rounded bg-slate-300 w-3/5' />
              </div>
            );
            case 'codeRequestForm_full': return (
              <div className='p-2 rounded border w-full h-12 flex flex-col justify-center gap-1 overflow-hidden'>
                <div className='h-[6px] rounded bg-slate-300 w-4/5' />
                <div className='h-[6px] rounded bg-slate-300 w-2/5' />
                <div className='h-[6px] rounded bg-slate-300 w-3/5' />
              </div>
            );
            case 'codeRequestGroup': return <div className='p-2 rounded border w-full h-12 flex items-center justify-center text-[10px] overflow-hidden'>Form Gruppe</div>;
            case 'codeRequestName': return <div className='p-2 rounded border w-full h-12 flex items-center overflow-hidden'><div className='h-[6px] rounded bg-slate-300 w-3/5' /></div>;
            case 'codeRequestEmail': return <div className='p-2 rounded border w-full h-12 flex items-center overflow-hidden'><div className='h-[6px] rounded bg-slate-300 w-4/5' /></div>;
            case 'codeRequestConsent': return <div className='p-2 rounded border w-full h-12 flex items-center gap-1 overflow-hidden'><div className='w-3 h-3 rounded border shrink-0' /><div className='h-[6px] rounded bg-slate-300 w-3/5' /></div>;
            case 'codeRequestDropdown': return (
              <div className='p-2 rounded border w-full h-12 flex flex-col justify-center gap-1 overflow-hidden'>
                <div className='h-[6px] rounded bg-slate-300 w-2/5' />
                <div className='h-[18px] rounded border bg-slate-50 w-full flex items-center px-1'><div className='h-[5px] rounded bg-slate-300 w-3/5' /></div>
              </div>
            );
            case 'codeRequestSubmit': return <div className='w-full h-12 flex items-center justify-center overflow-hidden'><div className='px-3 py-1 text-[10px] rounded bg-blue-600 text-white'>Absenden</div></div>;
            case 'codeDisplay': return <div className='p-2 rounded border w-full h-12 flex items-center justify-center font-mono text-[10px] bg-green-50 border-green-200 overflow-hidden'>ABCD-1234</div>;
            case 'emailCode': return <div className='p-2 rounded border w-full h-12 flex items-center justify-center font-mono text-[10px] bg-slate-50 overflow-hidden'>{'{{code}}'}</div>;
            case 'emailQr': return (
              <div className='p-2 rounded border w-full h-12 flex items-center justify-center gap-2 overflow-hidden'>
                <div className='w-6 h-6 bg-slate-200 border rounded' />
                <div className='h-[6px] rounded bg-slate-300 w-2/5' />
              </div>
            );
            case 'claimButton': return <div className='w-full h-12 flex items-center justify-center overflow-hidden'><div className='px-3 py-1 text-[10px] rounded bg-blue-600 text-white'>Code anzeigen</div></div>;
            case 'countdown': return (
              <div className='p-2 rounded border w-full h-12 flex items-center justify-center gap-2 overflow-hidden'>
                <div className='px-2 py-1 rounded border bg-background text-[10px]'>12</div>
                <div className='px-2 py-1 rounded border bg-background text-[10px]'>34</div>
                <div className='px-2 py-1 rounded border bg-background text-[10px]'>56</div>
              </div>
            );
            default: return <div className='p-2 rounded border w-full h-12 flex items-center justify-center text-[10px] overflow-hidden'>{type}</div>;
          }
        }
function Accordion({ title, children, defaultOpen=true }){
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className='border rounded'>
      <button type='button' onClick={()=>setOpen(o=>!o)} className='w-full flex items-center justify-between px-2 py-1 text-[11px] bg-slate-50 hover:bg-slate-100 border-b'>
        <span className='font-medium text-slate-700'>{title}</span>
        <span className='text-slate-500'>{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className='p-2'>{children}</div>}
    </div>
  );
}
